// R7-2 — THE ROOM CARDS AND THE SIDE-MENU ROOM LIST, MOVED VERBATIM OUT OF App.tsx.
//
// These two arrays ARE the home page: `generators` draws every room card, and
// `activeGenerator = generators.find((g) => g.id === page)` makes each card's id the routing key.
// `homeMenuItems` is the side menu's room list. They stayed inside App.tsx through R5 and R6 for
// one reason, named in R6's own pointer: they carry lucide-react JSX icons, and the gate's data-URL
// loader could not resolve a bare specifier, so moving them would have put the home page's whole
// content BEYOND the instrument roadmap 7 exists to build. R7-1 built `loadTsx` first; this move
// rides on it, and `scripts/r7-components.mjs` now loads these arrays and RUNS them.
//
// A .tsx and not a .ts because of the icons. Bodies unchanged — a "while I'm in here" edit inside a
// move is how a move becomes a regression.
import type { ReactNode } from "react";
import {
  BookOpen,
  CalendarDays,
  Globe,
  Hand,
  MessageCircle,
  Moon,
  Sofa,
  PenTool,
  Sparkles,
  SquareStack,
  Star,
  SunMoon,
  WandSparkles,
} from "lucide-react";
import dreamBackgroundUrl from "../assets/dream-background.webp";
import palmistryBackgroundUrl from "../assets/palmistry-background.webp";
import humanDesignBackgroundUrl from "../assets/human-design-background.webp";
import akashicBackgroundUrl from "../assets/akashic-background.webp";
import type { Page } from "../lib/pages";
// LR1 — the room's copy comes from the shared law, not from a string typed twice. The card, the
// page heading and the empty state read the same three constants the server reads.
import {
  ROOM_TITLE as LIVING_ROOM_TITLE,
  ROOM_DESCRIPTION as LIVING_ROOM_DESCRIPTION,
} from "../livingRoom.js";
import livingRoomBackgroundUrl from "../assets/living-room-background.webp";

export type Generator = {
  id: Exclude<Page, "home" | "library">;
  title: string;
  pageTitle?: string; // shown as the room's own H1 when it differs from the home-card title
  description: string;
  icon: ReactNode;
  detail: string;
  background?: string; // optional full-bleed room background webp (public/ path)
};

export type HomeMenuItem = {
  page: Page;
  title: string;
  menuTitle?: string; // MN1: the exact side-menu label (home cards keep `title` unchanged)
  description: string;
  icon: ReactNode;
  badge?: string;
};

export const generators: Generator[] = [
  {
    id: "spell-book",
    title: "Magic Room",
    icon: <WandSparkles />,
    description: "Browse ritual frameworks, ingredients, intentions, timing notes, and reflective spellwork guidance.",
    detail: "Conjure study room",
  },
  {
    id: "ask-lala",
    title: "Ask Lala",
    icon: <MessageCircle />,
    description: "Get intuitive answers, spiritual guidance, and energetic insight tailored to your unique path.",
    detail: "AI guidance room",
  },
  {
    id: "seals",
    title: "Create Your Own Seals",
    icon: <PenTool />,
    description: "Design symbolic seals from names, intentions, numbers, planetary timing, and personal spiritual focus.",
    detail: "Seal creator",
  },
  {
    id: "astrology",
    title: "Astrology",
    icon: <Star />,
    description: "Calculate your birth chart, transits, compatibility, career signatures, and timing.",
    detail: "Birth chart calculator",
  },
  {
    id: "numerology",
    title: "Numerology",
    icon: <CalendarDays />,
    description: "Decode life path, expression, soul urge, personal year, names, dates, and cycles.",
    detail: "Name and number reading",
  },
  {
    id: "dreams",
    title: "Dream Interpretation",
    icon: <Moon />,
    description: "Read symbols, colors, ancestors, emotions, recurring signs, and hidden messages.",
    detail: "Dream journal reader",
    background: dreamBackgroundUrl,
  },
  {
    id: "palmistry",
    title: "Palmistry",
    pageTitle: "Have Your Palm Read",
    icon: <Hand />,
    description: "Upload a photo of your open palm and have your lines, mounts, and markings read the old way.",
    detail: "Palm reading room",
    background: palmistryBackgroundUrl,
  },
  {
    id: "tarot",
    title: "Tarot",
    icon: <SquareStack />,
    description: "Pull daily cards, three-card spreads, spiritual advice, and reflection notes.",
    detail: "Card reading room",
  },
  {
    id: "human-design",
    title: "Human Design",
    pageTitle: "Decode Your Energetic Design",
    icon: <SunMoon />,
    description: "Explore type, strategy, authority, profile, centers, gates, and channels.",
    detail: "Design profile",
    background: humanDesignBackgroundUrl,
  },
  {
    id: "astrocartography",
    title: "Astrocartography",
    pageTitle: "Where Your Chart Works For You",
    icon: <Globe />,
    description: "Map your chart across the earth: read any place, and see what a move would really do.",
    detail: "Your map of the world",
  },
  {
    // AKASHIC-1 (her order, 2026-09-01). **NO pageTitle: her art carries the room heading.** She
    // typeset AKASHIC RECORDS and 12 HOUSE into both files, and the standing law is that anything
    // she has typeset into the image is never repeated as text beside it — so the room draws her
    // art and adds no heading of its own.
    id: "akashic",
    title: "Akashic Records",
    icon: <Sparkles />,
    description: "Five readings of your soul record: who you have been, what you carry, and what this life came here to master.",
    detail: "Soul record",
    background: akashicBackgroundUrl,
  },
  {
    // LR1-1 — THE LIVING ROOM joins the rooms list. Copy comes from src/livingRoom.js so the card,
    // the page heading and the empty state are one fact; the background is the staged asset, wired
    // through the same `background` field every other room's art uses.
    id: "living-room",
    title: LIVING_ROOM_TITLE,
    pageTitle: LIVING_ROOM_TITLE,
    icon: <Sofa />,
    description: LIVING_ROOM_DESCRIPTION,
    detail: "The community room",
    // Her art, through the same door every other room's art uses (LR1-12): `Living Room.png` and
    // `livingroom-vertical.png` at the repo root, fingerprinted into src/assets by
    // `npm run assets:backgrounds`, the vertical picked up by the phone media query in styles.css.
    background: livingRoomBackgroundUrl,
  },
  {
    id: "daily",
    title: "Daily Guidance",
    icon: <Sparkles />,
    description: "Receive a daily message based on timing, current intention, and saved patterns.",
    detail: "Daily spiritual check-in",
  },
];

export const homeMenuItems: HomeMenuItem[] = [
  {
    // LR1-13, HER RULING: **THE LIVING ROOM SITS DIRECTLY UNDER "MY ROOM".** In the desktop sidebar,
    // Home and My Room are the `site-sidebar__primary` block and this array renders immediately
    // beneath it, so FIRST HERE *IS* directly under My Room. The same array drives the mobile Rooms
    // sheet, which puts it first there too — "correspondingly prominent", from one ordering rather
    // than two that could drift apart.
    page: "living-room",
    title: LIVING_ROOM_TITLE,
    // No `menuTitle`: the R7 gate holds that a menuTitle identical to the title is dead weight, and
    // "The Living Room" is already the exact label the menu should show.
    icon: <Sofa />,
    description: LIVING_ROOM_DESCRIPTION,
    badge: "New",
  },
  {
    page: "spell-book",
    title: "Magic Room",
    icon: <WandSparkles />,
    description: "Browse ritual frameworks, ingredients, intentions, timing notes, and reflective spellwork guidance.",
  },
  {
    page: "ask-lala",
    title: "Ask Lala",
    menuTitle: "Ask Lala Room",
    icon: <MessageCircle />,
    description: "Get intuitive answers, spiritual guidance, and energetic insight tailored to your unique path.",
    badge: "New",
  },
  {
    page: "library",
    title: "Book & Video Library",
    menuTitle: "The Library",
    icon: <BookOpen />,
    description: "Read books, videos, teachings, notes, and transcripts inside a protected online reader.",
  },
  {
    page: "seals",
    title: "Create Your Own Seals",
    menuTitle: "Seal Creator Room",
    icon: <PenTool />,
    description: "Design symbolic seals from names, intentions, numbers, planetary timing, and personal spiritual focus.",
  },
  {
    page: "numerology",
    title: "Numerology",
    menuTitle: "Numerology Room",
    icon: <CalendarDays />,
    description: "Decode life path, expression, soul urge, personal year, names, dates, and cycles.",
  },
  {
    page: "astrology",
    title: "Astrology",
    menuTitle: "Astrology Room",
    icon: <Star />,
    description: "Calculate your birth chart, transits, compatibility, career signatures, and timing.",
  },
  {
    page: "tarot",
    title: "Tarot",
    menuTitle: "Tarot Room",
    icon: <SquareStack />,
    description: "Pull daily cards, three-card spreads, spiritual advice, and reflection notes.",
  },
  {
    page: "human-design",
    title: "Human Design",
    menuTitle: "Human Design Room",
    icon: <SunMoon />,
    description: "Type, strategy, authority, profile, centers, gates, and channels.",
  },
  {
    page: "astrocartography",
    title: "Astrocartography",
    icon: <Globe />,
    description: "Map your chart across the earth: read any place, and see what a move would really do.",
    badge: "New",
  },
  {
    // AKASHIC-1. R7 refused the push at 13 room cards against 12 menu rooms: a room on the home
    // page and not in the menu is a room half the house cannot find.
    page: "akashic",
    title: "Akashic Records",
    icon: <Sparkles />,
    description: "Five readings of your soul record: who you have been, what you carry, and what this life came here to master.",
    badge: "New",
  },
  {
    page: "dreams",
    title: "Dream Interpretation",
    icon: <Moon />,
    description: "Read symbols, colors, ancestors, emotions, recurring signs, and hidden messages.",
  },
  {
    page: "palmistry",
    title: "Palmistry",
    menuTitle: "Palmistry Room",
    icon: <Hand />,
    description: "Upload a photo of your palm and have your lines, mounts, and markings read.",
    badge: "New",
  },
];
